/**
 * Pricing item normalizer.
 *
 * Establishes a single canonical contract for snapshot/calculator pricing data:
 *   - `price` is in `priceCurrency` — that is the *region pricing currency*,
 *     not a UI preference.
 *   - `priceCurrency` is EUR for `region === 'eu'` and USD otherwise.
 *   - `displayCurrency` (UAH/USD/EUR) lives outside the data and is applied
 *     only at render time by `formatSnapshotPrice`.
 *
 * Why: backend products in EU return USD-numeric values labeled as EUR (the
 * EU pricing tier is its own list, expressed 1:1 in EUR). Phone numbers go
 * through a `convertToUSD` roundtrip when added from the catalog, so their
 * raw `price` is USD-numeric even when the catalog entry was EUR. Without
 * normalization, the right-hand panel and snapshot mix EUR-numeric (backend)
 * with USD-numeric (phone numbers), and `formatDisplayPrice` blindly labels
 * the sum as EUR — producing visible discrepancies between the calculator,
 * the offer page, and the comparison page.
 *
 * Legacy snapshots (no `priceCurrency` field) are still rendered correctly
 * via the fallback branch in `formatSnapshotPrice`.
 */

import {
  ceilWithEpsilon,
  convertUSDToDisplay,
  getCurrencySymbolByCode,
} from './currencyUtils';
import { i18n } from '../i18n/useI18n';

/** Region's primary pricing currency — what `price` is denominated in. */
export const getRegionPriceCurrency = (region) =>
  region === 'eu' ? 'EUR' : 'USD';

/**
 * Product types whose `price` is USD-numeric on the frontend even in EU mode
 * and therefore needs an explicit USD → EUR conversion to honour the region
 * pricing currency contract:
 *
 *   - phone-numbers: catalog prices are mixed-currency, converted to USD on
 *     insert (see PhoneNumbers/index.jsx).
 * Backend products (business-phone, fmc, call-tracking, callback,
 * ai-conversation, omnichannel-chat, eniq-ai, voicebot, voice-traffic) for EU
 * come back from the bundle API in the region pricing currency. Voice traffic
 * is already converted to EUR by the backend, despite legacy field names like
 * `total_cost_usd`, so converting it again here would understate EU prices.
 */
const USD_INTERNAL_PRODUCT_TYPES = new Set(['phone-numbers']);

/**
 * Normalize a raw pricing item to the region's pricing currency.
 *
 * @param {object} rawItem        Item with at least { price, productType }
 * @param {string} region         Calculator/snapshot region.
 * @param {object} currencyRates  NBU rates ({ usd, eur, kzt }).
 */
export const normalizePricingItemForRegion = (rawItem, region, currencyRates) => {
  const priceCurrency = getRegionPriceCurrency(region);
  const sourcePrice = Number(rawItem.price || 0);

  let price = sourcePrice;
  if (region === 'eu' && USD_INTERNAL_PRODUCT_TYPES.has(rawItem.productType)) {
    price = Number(convertUSDToDisplay(sourcePrice, 'EUR', currencyRates) || 0);
  }

  return {
    ...rawItem,
    price,
    priceCurrency,
    sourcePrice,
    sourceCurrency: 'USD',
  };
};

/**
 * Sum normalized phone-numbers contribution into a totalUSD-style accumulator,
 * using the same conversion rule as `normalizePricingItemForRegion` so the
 * total stays consistent with the items that compose it.
 *
 * Voice traffic does not need this helper because its contribution comes
 * straight from `bundleResult.total.primary.amount`, which the backend
 * already converts to EUR for the EU region.
 */
export const normalizePhoneLineForTotal = (linePriceUSD, region, currencyRates) => {
  if (region === 'eu') {
    return Number(convertUSDToDisplay(linePriceUSD, 'EUR', currencyRates) || 0);
  }
  return Number(linePriceUSD || 0);
};

/**
 * Convert between currencies using NBU rates (UAH-pivoted).
 * `rates.usd` = UAH per 1 USD, `rates.eur` = UAH per 1 EUR.
 */
const convertBetween = (amount, fromCurrency, toCurrency, rates) => {
  const value = Number(amount) || 0;
  if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) return value;
  if (!rates || typeof rates.usd !== 'number') return value;

  const toUAH = (amt, cur) => {
    if (cur === 'UAH') return amt;
    if (cur === 'USD') return amt * rates.usd;
    if (cur === 'EUR' && rates.eur) return amt * rates.eur;
    return amt;
  };
  const fromUAH = (amt, cur) => {
    if (cur === 'UAH') return amt;
    if (cur === 'USD') return amt / rates.usd;
    if (cur === 'EUR' && rates.eur) return amt / rates.eur;
    return amt;
  };

  return fromUAH(toUAH(value, fromCurrency), toCurrency);
};

/**
 * Resolve which currency the user actually sees, given snapshot region and
 * their UI preference. EU is locked to EUR; UA honours the USD/UAH toggle;
 * everywhere else falls back to USD.
 */
const getEffectiveDisplayCurrency = (region, displayCurrency) => {
  if (region === 'eu') return 'EUR';
  if (region === 'ua' && displayCurrency === 'UAH') return 'UAH';
  return 'USD';
};

const formatNumberWithSymbol = (amount, currency) => {
  // Match legacy `formatDisplayPrice` rounding (Math.ceil to whole units)
  // but use the epsilon-tolerant helper so 5×€3 = €15.0000001 → €15 instead
  // of jumping to €16. Phone numbers, voice traffic and EUR roundtrips can
  // produce these sub-LSB artefacts after currency conversion.
  const rounded = ceilWithEpsilon(amount);
  if (currency === 'UAH') {
    const symbol = i18n.t('currency.uahSymbol', 'UAH');
    return `${rounded} ${symbol}`;
  }
  return `${getCurrencySymbolByCode(currency)}${rounded}`;
};

/**
 * Format a normalized pricing item (or raw amount + explicit metadata) for
 * display. Handles three cases:
 *
 *   1. Modern: input has `priceCurrency` → convert from priceCurrency to the
 *      effective display currency, format.
 *   2. Legacy snapshot (no priceCurrency) + phone-numbers in EU → assume
 *      USD-internal and convert USD → EUR (preserves pre-fix snapshot output).
 *   3. Legacy fallback for everything else → mirrors `formatDisplayPrice`.
 *
 * @param {object|number} input
 * @param {object} opts
 * @param {string} opts.region          Snapshot/calculator region.
 * @param {string} opts.displayCurrency UI preference (USD/UAH/EUR).
 * @param {object} opts.rates           NBU rates.
 * @param {string} [opts.priceCurrency] Override / required if input is a number.
 * @param {string} [opts.productType]   Used by the legacy phone-numbers branch.
 */
export const formatSnapshotPrice = (input, opts = {}) => {
  const {
    region,
    displayCurrency = 'USD',
    rates = {},
  } = opts;

  let amount;
  let priceCurrency = opts.priceCurrency;
  let productType = opts.productType;

  if (input != null && typeof input === 'object') {
    amount = Number(input.price || 0);
    priceCurrency = input.priceCurrency || priceCurrency;
    productType = input.productType || productType;
  } else {
    amount = Number(input || 0);
  }

  const effectiveDisplay = getEffectiveDisplayCurrency(region, displayCurrency);

  // Modern path: explicit priceCurrency.
  if (priceCurrency) {
    const converted = convertBetween(amount, priceCurrency, effectiveDisplay, rates);
    return formatNumberWithSymbol(converted, effectiveDisplay);
  }

  // Legacy fallback: pre-normalisation snapshots / call sites.
  if (region === 'eu') {
    if (USD_INTERNAL_PRODUCT_TYPES.has(productType)) {
      // Phone numbers were stored USD-internal — convert before showing as EUR.
      // Mirrors normalizePricingItemForRegion.
      const eur = Number(convertUSDToDisplay(amount, 'EUR', rates) || 0);
      return formatNumberWithSymbol(eur, 'EUR');
    }
    // Backend products: number is already in the EU pricing currency.
    return formatNumberWithSymbol(amount, 'EUR');
  }

  if (region === 'ua' && displayCurrency === 'UAH') {
    const uah = Number(amount) * (rates.usd || 0);
    return formatNumberWithSymbol(uah, 'UAH');
  }

  return formatNumberWithSymbol(amount, 'USD');
};
